import { Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal, computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { applyEach, FieldTree, form, required } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { AsetustenOletuksetService } from '../../_angular/service/asetusten-oletukset.service'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

import { Laskuasetukset, LaskuasetuksetSpostille, LaskunSahkopostipohja, SAHKOPOSTIPOHJAN_MUUTTUJAT } from '@lemon/core-firebase/model/lasku'
import { LaskuSpostiService } from '@lemon/core-firebase/service/lasku/lasku-sposti.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { TuettuKieli } from '@lemon/core/model/common'

import { merge, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { LaskutusprofiiliService } from '../../_angular/service/lasku/laskutusprofiili.service'

interface SahkopostipohjanMuuttuja {
  merkinta: string
  nimi: string
}

interface KielenSpostipohjatLomake {
  aihe: string
  otsikko: string
  teksti: string
  maihe: string
  motsikko: string
  mteksti: string
  haihe: string
  hotsikko: string
  hteksti: string
}

interface EmailpohjaLomake {
  slogan: string
  spostiasetukset: KielenSpostipohjatLomake[]
}

@Component({
  selector: 'app-emailpohja',
  templateUrl: './emailpohja.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmailPohjaComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput) replyToInput: MatInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private tuetutKielet: TuettuKieli[] = ['fi', 'en' /* , 'sv' */]
  private readonly _injector = inject(Injector)
  private _kieletVaihtuvat = new Subject<void>()
  private readonly _naytetytKielet = signal<ReadonlySet<number>>(new Set([0]))

  lomake = form(signal<EmailpohjaLomake>({ slogan: '', spostiasetukset: [] }), polku => {
    applyEach(polku.spostiasetukset, kieli => {
      required(kieli.aihe)
      required(kieli.otsikko)
      // Tekstin pakollisuus tuli templaten required-attribuutista: se oli voimassa vain näytetyllä välilehdellä, ja sen virhe jäi voimaan välilehteä vaihdettaessa
      required(kieli.teksti, { when: ({ pathKeys }) => this._naytetytKielet().has(Number(pathKeys()[1])) })
      required(kieli.maihe)
      required(kieli.motsikko)
      required(kieli.haihe)
      required(kieli.hotsikko)
    })
  })
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('laskuasetukset', this.lomake)
  })

  asetukset: Laskuasetukset = null
  valittuPohja: 'lasku' | 'muistutus' | 'hyvitys' = 'lasku'
  valittuKieliIndeksi = 0

  constructor(
    private laskuService: LaskuService,
    private validationService: FormValidationService,
    private translationService: LemonTranslationService,
    private laskuKopioija: LaskuKopioija,
    private asetuksetOletuksetService: AsetustenOletuksetService,
    private errorHandler: ErrorHandler,
    private snackbar: MatSnackBar,
    private ladataanService: LadataanService,
    private _laskuSpostiService: LaskuSpostiService,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {}

  private lisaaKieli(kieli: TuettuKieli, asetukset: LaskuasetuksetSpostille, muistutusasetukset: LaskuasetuksetSpostille, hyvitysasetukset: LaskuasetuksetSpostille) {
    const spostiasetukset = this.lomake.spostiasetukset().value()
    asetaArvo(
      this.lomake.spostiasetukset,
      [
        ...spostiasetukset,
        {
          aihe: asetukset.aihe ?? '',
          otsikko: asetukset.otsikko ?? '',
          teksti: asetukset.teksti ?? '',
          maihe: muistutusasetukset.aihe ?? '',
          motsikko: muistutusasetukset.otsikko ?? '',
          mteksti: muistutusasetukset.teksti ?? '',
          haihe: hyvitysasetukset.aihe ?? '',
          hotsikko: hyvitysasetukset.otsikko ?? '',
          hteksti: hyvitysasetukset.teksti ?? '',
        },
      ],
      { ilmoita: false },
    )
    const kielenSpostiasetukset = this.lomake.spostiasetukset[spostiasetukset.length]
    const lopetus = merge(this.ngUnsubscribe, this._kieletVaihtuvat)
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(lopetus))

    muutokset(kielenSpostiasetukset.haihe).subscribe(value => {
      hyvitysasetukset.aihe = this.validationService.processValue(value)
    })
    muutokset(kielenSpostiasetukset.hotsikko).subscribe(value => {
      hyvitysasetukset.otsikko = this.validationService.processValue(value)
    })
    muutokset(kielenSpostiasetukset.hteksti).subscribe(value => {
      hyvitysasetukset.teksti = this.validationService.sanitizeWhitespace(value)
    })

    muutokset(kielenSpostiasetukset.maihe).subscribe(value => {
      muistutusasetukset.aihe = this.validationService.processValue(value)
    })
    muutokset(kielenSpostiasetukset.motsikko).subscribe(value => {
      muistutusasetukset.otsikko = this.validationService.processValue(value)
    })
    muutokset(kielenSpostiasetukset.mteksti).subscribe(value => {
      muistutusasetukset.teksti = this.validationService.sanitizeWhitespace(value)
    })
    muutokset(kielenSpostiasetukset.aihe).subscribe(value => {
      asetukset.aihe = this.validationService.processValue(value)
    })
    muutokset(kielenSpostiasetukset.otsikko).subscribe(value => {
      asetukset.otsikko = this.validationService.processValue(value)
    })
    muutokset(kielenSpostiasetukset.teksti).subscribe(value => {
      asetukset.teksti = this.validationService.sanitizeWhitespace(value)
    })
  }

  ngOnInit() {
    // Bind to changes
    // this.form.get('replyto').valueChanges.subscribe(value => {this.asetukset.replyto = value})
    arvonMuutokset(this.lomake.slogan, { injector: this._injector }).subscribe(value => {
      this.asetukset.slogan = value
    })
    // this.form.get('template').valueChanges.subscribe(value => {this.asetukset.template = value})

    // Load data
    this._laskutusprofiiliService.valittuLaskutusprofiiliObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      asetukset => {
        // this.form.reset()
        if (asetukset) {
          this.asetukset = this.laskuKopioija.copyAsetukset(asetukset)
          this.asetuksetOletuksetService.poistaOletuksetAsetuksista(this.asetukset)
        } else {
          this.asetukset = {
            avain: null,
            nimi: null,
            ytunnus: null,
            katuosoite: null,
            postitmp: null,
            postinro: null,
            maakoodi: 'FIN',
            date: null,
            replyto: null,
            puhelin: null,
            bic: null,
            iban: null,
            pankki: null,
            base64Logo: null,
            ohjeet: null,
            slogan: null,
            piilotaYrityksenNimiLaskulla: false,
            summahuomautusPiilotettu: false,
            muistutusSpostiasetukset: {},
            hyvitysSpostiasetukset: {},
            spostiasetukset: {},
            tervetuloaNahty: true,
          }
        }

        this.asetukset.tervetuloaNahty = true

        if (!this.asetukset.spostiasetukset) {
          this.asetukset.spostiasetukset = {}
        }

        if (!this.asetukset.muistutusSpostiasetukset) {
          this.asetukset.muistutusSpostiasetukset = {}
        }

        if (!this.asetukset.hyvitysSpostiasetukset) {
          this.asetukset.hyvitysSpostiasetukset = {}
        }

        this._kieletVaihtuvat.next()
        this._naytetytKielet.set(new Set([this.valittuKieliIndeksi]))
        asetaArvo(this.lomake.spostiasetukset, [], { ilmoita: false })
        for (const tuettuKieli of this.tuetutKielet) {
          let spostiasetukset: LaskuasetuksetSpostille = this.asetukset.spostiasetukset[tuettuKieli]
          let muistutusSpostiasetukset: LaskuasetuksetSpostille = this.asetukset.muistutusSpostiasetukset[tuettuKieli]
          let hyvitysSpostiasetukset = this.asetukset.hyvitysSpostiasetukset[tuettuKieli]

          if (!spostiasetukset) {
            spostiasetukset = {
              aihe: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.sposti.aihe', tuettuKieli),
              kieli: tuettuKieli,
              otsikko: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.sposti.otsikko', tuettuKieli),
              teksti: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.sposti.teksti', tuettuKieli),
              template: LaskunSahkopostipohja.PERINTEINEN,
            }
            this.asetukset.spostiasetukset[tuettuKieli] = spostiasetukset
          }

          if (!muistutusSpostiasetukset) {
            muistutusSpostiasetukset = {
              aihe: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.muistutussposti.aihe', tuettuKieli),
              kieli: tuettuKieli,
              otsikko: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.muistutussposti.otsikko', tuettuKieli),
              teksti: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.muistutussposti.teksti', tuettuKieli),
              template: LaskunSahkopostipohja.MUISTUTUS_PERINTEINEN,
            }
            this.asetukset.muistutusSpostiasetukset[tuettuKieli] = muistutusSpostiasetukset
          }

          if (!hyvitysSpostiasetukset) {
            hyvitysSpostiasetukset = {
              aihe: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.hyvityssposti.aihe', tuettuKieli),
              kieli: tuettuKieli,
              otsikko: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.hyvityssposti.otsikko', tuettuKieli),
              teksti: this.translationService.lokalisoiKielella('lasku.spostipohjat.oletukset.hyvityssposti.teksti', tuettuKieli),
              template: LaskunSahkopostipohja.HYVITYS_PERINTEINEN,
            }
            this.asetukset.hyvitysSpostiasetukset[tuettuKieli] = hyvitysSpostiasetukset
          }

          this._muunnaVanhatMuuttujatLuettaviksi(spostiasetukset, tuettuKieli)
          this._muunnaVanhatMuuttujatLuettaviksi(muistutusSpostiasetukset, tuettuKieli)
          this._muunnaVanhatMuuttujatLuettaviksi(hyvitysSpostiasetukset, tuettuKieli)

          // console.log(spostiasetukset, muistutusSpostiasetukset)

          this.lisaaKieli(tuettuKieli, spostiasetukset, muistutusSpostiasetukset, hyvitysSpostiasetukset)
        }

        // this.form.get('replyto').setValue(this.asetukset.replyto)
        // this.form.get('template').setValue(this.asetukset.template)
        // Kuuntelija kopioi arvon sellaisenaan, joten ilmoittamatta asetuksiin jää null eikä tekstikentän tyhjä merkkijono
        asetaArvo(this.lomake.slogan, this.asetukset.slogan ?? '', { ilmoita: false })
        this.lomake().reset()
      },
      err => {
        this.errorHandler.handleError(err)
      },
    )
  }

  valitseKieli(indeksi: number) {
    this.valittuKieliIndeksi = indeksi
    this._naytetytKielet.update(naytetyt => new Set([...naytetyt, indeksi]))
  }

  get pohjanMuuttujat(): SahkopostipohjanMuuttuja[] {
    const kieli = this.tuetutKielet[this.valittuKieliIndeksi]
    return Object.values(SAHKOPOSTIPOHJAN_MUUTTUJAT).map(merkinnat => ({ merkinta: merkinnat[kieli], nimi: merkinnat[kieli].slice(1, -1) }))
  }

  lisaaMuuttuja(kentta: HTMLInputElement | HTMLTextAreaElement, kentanNimi: keyof KielenSpostipohjatLomake, muuttuja: string) {
    const lomakkeenKentta = this.lomake.spostiasetukset[this.valittuKieliIndeksi][kentanNimi]
    const teksti: string = lomakkeenKentta().value() ?? ''
    const tekstiEnnen = teksti.slice(0, kentta.selectionStart)
    const tekstiJalkeen = teksti.slice(kentta.selectionEnd)
    const lisattava = this._annaLisattavaMuuttuja(tekstiEnnen, muuttuja)
    asetaArvo(lomakkeenKentta, tekstiEnnen + lisattava + tekstiJalkeen)
    lomakkeenKentta().markAsDirty()
    this._siirraKohdistin(kentta, (tekstiEnnen + lisattava).length)
  }

  private _annaLisattavaMuuttuja(tekstiEnnen: string, muuttuja: string): string {
    const tarvitaankoValilyonti = tekstiEnnen.length > 0 && !tekstiEnnen.endsWith(' ') && !tekstiEnnen.endsWith('\n')
    return tarvitaankoValilyonti ? ' ' + muuttuja : muuttuja
  }

  private _siirraKohdistin(kentta: HTMLInputElement | HTMLTextAreaElement, paikka: number) {
    requestAnimationFrame(() => {
      kentta.focus()
      kentta.setSelectionRange(paikka, paikka)
    })
  }

  private _muunnaVanhatMuuttujatLuettaviksi(asetukset: LaskuasetuksetSpostille, kieli: TuettuKieli) {
    asetukset.aihe = this._laskuSpostiService.muunnaVanhatMuuttujatLuettaviksi(asetukset.aihe, kieli)
    asetukset.otsikko = this._laskuSpostiService.muunnaVanhatMuuttujatLuettaviksi(asetukset.otsikko, kieli)
    asetukset.teksti = this._laskuSpostiService.muunnaVanhatMuuttujatLuettaviksi(asetukset.teksti, kieli)
  }

  otsikko(i: number): string {
    const kieli = this.tuetutKielet[i]
    return this.translationService.lokalisoi('kieli.' + kieli)
  }

  onkoTallentamattomiaMuutoksia(): boolean {
    return this.lomake().dirty()
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  save() {
    if (this.validationService.onkoLomakkeessaVirheita(this.lomake)) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
    } else {
      this.ladataanService.aloitaLataaminen()
      this.laskuService
        .updateLaskuasetukset(this.asetukset, this.asetukset.avain)
        .then(() => {
          const lokalisoitu = this.translationService.lokalisoi('laskuasetukset.tallennettu-onnistuneesti')
          this.snackbar.open(lokalisoitu, 'OK', { duration: 5000, verticalPosition: 'top' })
          this.ladataanService.lopetaLataaminen()
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
    }
  }
}
